import React, { useState } from 'react';
import { Palette, Eye, Type, Zap, Volume2, VolumeX, X, Check } from 'lucide-react';
import { ThemeMode } from '../hooks/useTheme';
import { ChildTheme, useChildTheme } from '../hooks/useChildTheme';
import Logo from './Logo';

interface ChildThemePickerProps {
  isOpen: boolean;
  onClose: () => void;
  childId: string;
  childName: string;
}

export function ChildThemePicker({ isOpen, onClose, childId, childName }: ChildThemePickerProps) {
  const { childTheme, updateChildTheme } = useChildTheme(childId);
  const [previewTheme, setPreviewTheme] = useState<Partial<ChildTheme> | null>(null);

  if (!isOpen || !childTheme) return null;

  const currentTheme = previewTheme ? { ...childTheme, ...previewTheme } : childTheme;

  const themeOptions: Array<{
    mode: ThemeMode;
    name: string;
    description: string;
    colors: string[];
    benefits: string[];
  }> = [
    {
      mode: 'light',
      name: 'Bright & Clear',
      description: 'Clean white backgrounds with clear text',
      colors: ['#ffffff', '#f8fafc', '#3b82f6', '#10b981'],
      benefits: ['High readability', 'Familiar interface', 'Good for focus']
    },
    {
      mode: 'dark',
      name: 'Dark & Cozy',
      description: 'Dark backgrounds that are easy on the eyes',
      colors: ['#0a0f1c', '#1a2332', '#60a5fa', '#34d399'],
      benefits: ['Reduces eye strain', 'Better for low light', 'Calming effect']
    },
    {
      mode: 'high-contrast',
      name: 'Super Clear',
      description: 'Maximum contrast for the clearest view',
      colors: ['#000000', '#ffffff', '#00bfff', '#00ff00'],
      benefits: ['Maximum readability', 'Clear boundaries', 'Accessibility focused']
    },
    {
      mode: 'calm',
      name: 'Ocean Calm',
      description: 'Soft blues and gentle colors for relaxation',
      colors: ['#f0f9ff', '#e0f2fe', '#0ea5e9', '#06b6d4'],
      benefits: ['Reduces anxiety', 'Promotes focus', 'Soothing colors']
    },
    {
      mode: 'warm',
      name: 'Sunny Warm',
      description: 'Warm yellows and oranges for comfort',
      colors: ['#fefcf3', '#fef3c7', '#f59e0b', '#92400e'],
      benefits: ['Feels welcoming', 'Boosts mood', 'Comfortable reading']
    }
  ];

  const handlePreview = (newTheme: Partial<ChildTheme>) => {
    const updated = { ...previewTheme, ...newTheme };
    setPreviewTheme(updated);
    
    // Apply the preview theme immediately to the global theme system
    // This ensures the UI updates in real-time
    const globalTheme = {
      mode: updated.mode || currentTheme.mode,
      reducedMotion: updated.reducedMotion !== undefined ? updated.reducedMotion : currentTheme.reducedMotion,
      fontSize: updated.fontSize || currentTheme.fontSize,
      colorIntensity: updated.colorIntensity || currentTheme.colorIntensity
    };
    
    // Apply to document root for immediate visual feedback
    const root = document.documentElement;
    root.classList.remove('theme-light', 'theme-dark', 'theme-high-contrast', 'theme-calm', 'theme-warm');
    root.classList.add(`theme-${globalTheme.mode}`);
    
    if (globalTheme.reducedMotion) {
      root.classList.add('reduced-motion');
    } else {
      root.classList.remove('reduced-motion');
    }
    
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${globalTheme.fontSize}`);
    
    root.classList.remove('color-low', 'color-medium', 'color-high');
    root.classList.add(`color-${globalTheme.colorIntensity}`);
    
    // Apply CSS custom properties
    applyThemeVariables(globalTheme);
  };

  const applyThemeVariables = (themeConfig: any) => {
    const root = document.documentElement;
    
    // Enhanced color palettes with improved contrast for all themes
    const colorPalettes = {
      light: {
        '--bg-primary': '#ffffff',
        '--bg-secondary': '#f8fafc',
        '--bg-tertiary': '#f1f5f9',
        '--text-primary': '#0f172a', // Darker for better contrast
        '--text-secondary': '#334155', // Darker for better readability
        '--text-muted': '#64748b',
        '--border-color': '#e2e8f0',
        '--accent-blue': '#2563eb', // Slightly darker for better contrast
        '--accent-green': '#059669', // Darker for better contrast
        '--accent-purple': '#7c3aed', // Darker for better contrast
        '--accent-orange': '#ea580c', // Darker for better contrast
        '--accent-red': '#dc2626', // Darker for better contrast
        '--accent-yellow': '#ca8a04', // Darker for better contrast
        '--accent-cyan': '#0891b2', // Darker for better contrast
        '--accent-teal': '#0d9488', // Darker for better contrast
        '--shadow': '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      },
      dark: {
        // Enhanced dark theme with better contrast
        '--bg-primary': '#0a0f1c', // Darker for better contrast
        '--bg-secondary': '#1a2332', // Slightly lighter than primary
        '--bg-tertiary': '#2a3441', // Lighter for layering
        '--text-primary': '#ffffff', // Pure white for maximum contrast
        '--text-secondary': '#e2e8f0', // Lighter for better readability
        '--text-muted': '#94a3b8',
        '--border-color': '#3f4956', // Lighter for visibility
        '--accent-blue': '#60a5fa', // Bright for visibility
        '--accent-green': '#34d399', // Bright for visibility
        '--accent-purple': '#a78bfa', // Bright for visibility
        '--accent-orange': '#fbbf24', // Bright for visibility
        '--accent-red': '#f87171', // Bright for visibility
        '--accent-yellow': '#fde047', // Bright for visibility
        '--accent-cyan': '#22d3ee', // Bright for visibility
        '--accent-teal': '#2dd4bf', // Bright for visibility
        '--shadow': '0 4px 6px 0 rgb(0 0 0 / 0.5)', // Stronger shadow for depth
      },
      'high-contrast': {
        '--bg-primary': '#000000',
        '--bg-secondary': '#1a1a1a',
        '--bg-tertiary': '#333333',
        '--text-primary': '#ffffff',
        '--text-secondary': '#e0e0e0',
        '--text-muted': '#cccccc',
        '--border-color': '#666666',
        '--accent-blue': '#00bfff',
        '--accent-green': '#00ff00',
        '--accent-purple': '#ff00ff',
        '--accent-orange': '#ffa500',
        '--accent-red': '#ff0000',
        '--accent-yellow': '#ffff00',
        '--accent-cyan': '#00ffff',
        '--accent-teal': '#00ffbf',
        '--shadow': '0 2px 4px 0 rgb(255 255 255 / 0.1)',
      },
      calm: {
        '--bg-primary': '#f0f9ff',
        '--bg-secondary': '#e0f2fe',
        '--bg-tertiary': '#bae6fd',
        '--text-primary': '#0c4a6e',
        '--text-secondary': '#0369a1',
        '--text-muted': '#0284c7',
        '--border-color': '#7dd3fc',
        '--accent-blue': '#0ea5e9',
        '--accent-green': '#06b6d4',
        '--accent-purple': '#8b5cf6',
        '--accent-orange': '#f59e0b',
        '--accent-red': '#ef4444',
        '--accent-yellow': '#eab308',
        '--accent-cyan': '#0891b2',
        '--accent-teal': '#0d9488',
        '--shadow': '0 1px 3px 0 rgb(14 165 233 / 0.1)',
      },
      warm: {
        '--bg-primary': '#fefcf3',
        '--bg-secondary': '#fef3c7',
        '--bg-tertiary': '#fde68a',
        '--text-primary': '#78350f',
        '--text-secondary': '#92400e',
        '--text-muted': '#a16207',
        '--border-color': '#fbbf24',
        '--accent-blue': '#3b82f6',
        '--accent-green': '#10b981',
        '--accent-purple': '#8b5cf6',
        '--accent-orange': '#f59e0b',
        '--accent-red': '#ef4444',
        '--accent-yellow': '#eab308',
        '--accent-cyan': '#06b6d4',
        '--accent-teal': '#14b8a6',
        '--shadow': '0 1px 3px 0 rgb(245 158 11 / 0.1)',
      }
    };
    
    // Apply base colors
    const palette = colorPalettes[themeConfig.mode as keyof typeof colorPalettes];
    Object.entries(palette).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Apply font size scaling
    const fontSizes = {
      small: '0.875',
      medium: '1',
      large: '1.125'
    };
    root.style.setProperty('--font-scale', fontSizes[themeConfig.fontSize as keyof typeof fontSizes]);
    
    // Apply color intensity
    const intensities = {
      low: '0.7',
      medium: '1',
      high: '1.3'
    };
    root.style.setProperty('--color-intensity', intensities[themeConfig.colorIntensity as keyof typeof intensities]);
  };

  const handleApply = async () => {
    if (previewTheme) {
      await updateChildTheme(previewTheme);
    }
    onClose();
  };

  const handleCancel = () => {
    // Revert to original theme
    const root = document.documentElement;
    root.classList.remove('theme-light', 'theme-dark', 'theme-high-contrast', 'theme-calm', 'theme-warm');
    root.classList.add(`theme-${childTheme.mode}`);
    
    if (childTheme.reducedMotion) {
      root.classList.add('reduced-motion');
    } else {
      root.classList.remove('reduced-motion');
    }
    
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${childTheme.fontSize}`);
    
    root.classList.remove('color-low', 'color-medium', 'color-high');
    root.classList.add(`color-${childTheme.colorIntensity}`);
    
    applyThemeVariables(childTheme);
    
    setPreviewTheme(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-primary)] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-[var(--border-color)]">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-blue-500 px-6 py-4 text-white rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Logo size="lg" variant="icon-only" />
              <div>
                <h2 className="text-xl font-bold font-baloo">{childName}'s Theme Settings</h2>
                <p className="text-blue-100">Make your learning space just right for you!</p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Theme Selection */}
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center space-x-2 font-baloo">
              <Eye className="w-5 h-5" />
              <span>Choose Your Colors</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {themeOptions.map((option) => (
                <button
                  key={option.mode}
                  onClick={() => handlePreview({ mode: option.mode })}
                  className={`p-4 rounded-xl border-2 text-left transition-all hover:scale-105 ${
                    currentTheme.mode === option.mode
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-[var(--border-color)] hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="flex space-x-1">
                      {option.colors.map((color, index) => (
                        <div
                          key={index}
                          className="w-4 h-4 rounded-full border border-gray-300"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    {currentTheme.mode === option.mode && (
                      <Check className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                  
                  <h4 className="font-semibold text-[var(--text-primary)] mb-1 font-baloo">{option.name}</h4>
                  <p className="text-sm text-[var(--text-secondary)] mb-3">{option.description}</p>
                  
                  <div className="space-y-1">
                    {option.benefits.map((benefit, index) => (
                      <div key={index} className="text-xs text-[var(--text-muted)] flex items-center space-x-1">
                        <div className="w-1 h-1 bg-[var(--accent-green)] rounded-full" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Accessibility Options */}
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center space-x-2 font-baloo">
              <Zap className="w-5 h-5" />
              <span>Make It Comfortable</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Font Size */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-3 font-baloo">
                  Text Size
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'small', label: 'Small', sample: 'The quick brown fox' },
                    { value: 'medium', label: 'Medium', sample: 'The quick brown fox' },
                    { value: 'large', label: 'Large', sample: 'The quick brown fox' }
                  ].map((size) => (
                    <button
                      key={size.value}
                      onClick={() => handlePreview({ fontSize: size.value as any })}
                      className={`w-full p-3 rounded-lg border text-left transition-colors ${
                        currentTheme.fontSize === size.value
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-[var(--border-color)] hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-[var(--text-primary)] font-baloo">{size.label}</div>
                          <div 
                            className="text-[var(--text-secondary)]"
                            style={{ 
                              fontSize: size.value === 'small' ? '0.875rem' : 
                                       size.value === 'large' ? '1.125rem' : '1rem' 
                            }}
                          >
                            {size.sample}
                          </div>
                        </div>
                        {currentTheme.fontSize === size.value && (
                          <Check className="w-4 h-4 text-purple-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Intensity */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-3 font-baloo">
                  Color Brightness
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'low', label: 'Gentle', description: 'Soft, muted colors' },
                    { value: 'medium', label: 'Balanced', description: 'Standard brightness' },
                    { value: 'high', label: 'Vibrant', description: 'Bold, bright colors' }
                  ].map((intensity) => (
                    <button
                      key={intensity.value}
                      onClick={() => handlePreview({ colorIntensity: intensity.value as any })}
                      className={`w-full p-3 rounded-lg border text-left transition-colors ${
                        currentTheme.colorIntensity === intensity.value
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-[var(--border-color)] hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-[var(--text-primary)] font-baloo">{intensity.label}</div>
                          <div className="text-sm text-[var(--text-secondary)]">{intensity.description}</div>
                        </div>
                        {currentTheme.colorIntensity === intensity.value && (
                          <Check className="w-4 h-4 text-purple-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Motion Settings */}
            <div className="mt-6">
              <div className="flex items-center justify-between p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[var(--accent-blue)] bg-opacity-10 rounded-lg">
                    {currentTheme.reducedMotion ? (
                      <VolumeX className="w-5 h-5 text-[var(--accent-blue)]" />
                    ) : (
                      <Volume2 className="w-5 h-5 text-[var(--accent-blue)]" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-[var(--text-primary)] font-baloo">Calm Animations</div>
                    <div className="text-sm text-[var(--text-secondary)]">
                      Make things move more slowly
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handlePreview({ reducedMotion: !currentTheme.reducedMotion })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    currentTheme.reducedMotion ? 'bg-[var(--accent-blue)]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      currentTheme.reducedMotion ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 font-baloo">See How It Looks</h3>
            <div className="bg-[var(--bg-secondary)] rounded-xl p-6 border border-[var(--border-color)]">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Logo size="xl" variant="icon-only" />
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--text-primary)] font-baloo">Neuro's Activity</h4>
                    <p className="text-[var(--text-secondary)]">This is how your activities will look</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <button className="p-3 bg-[var(--accent-green)] text-white rounded-lg font-medium hover:opacity-90 transition-opacity font-baloo">
                    Start Learning
                  </button>
                  <button className="p-3 bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg font-medium border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors font-baloo">
                    View Progress
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-4 border-t border-[var(--border-color)]">
            <button
              onClick={handleCancel}
              className="flex-1 px-6 py-3 border-2 border-[var(--border-color)] text-[var(--text-primary)] rounded-xl hover:bg-[var(--bg-secondary)] font-semibold transition-colors font-baloo"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-xl hover:opacity-90 font-semibold transition-opacity font-baloo"
            >
              Save My Theme
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}